import React, { useCallback, useContext, useState } from 'react';
import LocalStorage from '../lib/local-storage';
import { useTwilioUserMediaContext } from './TwilioUserMediaContext';

export interface SelfViewContextValues {
    turnedOff: boolean;
    turnOff: () => void;
    turnOn: () => void;
    destroy: () => void;
}
const defaultValues: SelfViewContextValues = {
    turnedOff: true,
    turnOff: () => {},
    turnOn: () => {},
    destroy: () => {},
};
export const SelfViewContext = React.createContext<SelfViewContextValues>(defaultValues);

export const SelfViewConsumer = SelfViewContext.Consumer;

const TURN_OFF_SELFVIEW = 'TURN_OFF_SELFVIEW';

export const SelfViewProvider: React.FunctionComponent<React.PropsWithChildren> = ({
    children,
}) => {
    const { audioDeviceId, videoDeviceId, startMedia, stopMedia } = useTwilioUserMediaContext();

    const [turnedOff, setTurnedOff] = useState(
        LocalStorage.get(TURN_OFF_SELFVIEW, 'false') === 'true',
    );

    const turnOff = useCallback(() => {
        stopMedia();
        setTurnedOff(true);
        LocalStorage.set(TURN_OFF_SELFVIEW, true);
    }, [stopMedia]);

    const turnOn = useCallback(() => {
        startMedia({
            type: 'exact',
            audioDeviceId: audioDeviceId ?? '',
            videoDeviceId: videoDeviceId ?? '',
        });
        setTurnedOff(false);
        LocalStorage.set(TURN_OFF_SELFVIEW, false);
    }, [audioDeviceId, videoDeviceId, startMedia]);

    const destroy = useCallback(() => {
        setTurnedOff(false);
        LocalStorage.remove(TURN_OFF_SELFVIEW);
    }, []);

    return (
        <SelfViewContext.Provider value={{ turnedOff, turnOff, turnOn, destroy }}>
            {children}
        </SelfViewContext.Provider>
    );
};

export const useSelfViewContext = () => useContext(SelfViewContext);
